import { useMemo, useState } from "react";
import PageList from '../../templates/PageList';
import BadgeStatus from '../../atoms/BadgeStatus';
import PageSearch from '../../molecules/PageSearch';
import appConfig from '../../../config/app-config';
import { Auth } from 'aws-amplify';
import ReversalModal from './modals/ReversalModal';
import PaymentModal from './modals/PaymentModal';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink';

const statusColors: Record<string, BadgeColor> = {
    OPEN: 'yellow',
    PAID: 'green',
    PARTIALLY_PAID: 'orange',
    SUPERSEDED: 'gray',
    CANCELLED: 'red',
    PAID_WITH_LOSS: 'orange',
};

const movementTypeLabels: Record<string, string> = {
    TOTAL_PAYMENT: 'Pagamento Total',
    PARTIAL_PAYMENT: 'Pagamento Parcial',
    RENEGOTIATION: 'Renegociação',
    REVERSAL: 'Estorno',
};

const settlementTypeLabels: Record<number, string> = {
    1: 'Total',
    2: 'Parcial',
    3: 'Em Atraso',
    4: 'Antecipado',
};

const formatCurrency = (value: string) => {
    const num = parseFloat(value || '0');
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (date: string | null) => {
    if (!date) return '-';
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
};

const TitleDebtPageList = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [currentSortField, setCurrentSortField] = useState('due_date');
    const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [selectedDebt, setSelectedDebt] = useState<any>(null);
    const [movements, setMovements] = useState<any[]>([]);
    const [loadingMovements, setLoadingMovements] = useState(false);
    const [searchContract, setSearchContract] = useState('');
    const [filterStatus, setFilterStatus] = useState('OPEN');
    const [filterModality, setFilterModality] = useState('');
    const [filterNegotiation, setFilterNegotiation] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

    const sortParam = currentSortOrder === 'DESC' ? `-${currentSortField}` : currentSortField;
    const params = new URLSearchParams({
        page: String(currentPage + 1),
        size: String(currentPageSize),
        sort: sortParam,
    });
    if (searchContract) params.set('filter[contract_number]', searchContract);
    if (filterStatus) params.set('filter[status]', filterStatus);
    if (filterModality) params.set('filter[modality]', filterModality);
    if (filterNegotiation) params.set('filter[negotiation_type]', filterNegotiation);
    const url = `${API_URL}/title-debts?${params.toString()}&_r=${refreshKey}`;

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API_KEY || '' }
    ], [API_KEY]);

    const tableHeaders = useMemo(() => [
        { id: 'contract_number', title: 'Contrato', canSort: true, field: 'contract_number' },
        { id: 'document_title', title: 'Título/NF', canSort: false },
        { id: 'contract_operation_id', title: 'Operação', canSort: false },
        { id: 'installment_number', title: 'Parcela', canSort: true, field: 'installment_number' },
        { id: 'amendment_number', title: 'Aditamento', canSort: true, field: 'amendment_number' },
        { id: 'amount', title: 'Valor', canSort: true, field: 'amount' },
        { id: 'paid_amount', title: 'Pago', canSort: true, field: 'paid_amount' },
        { id: 'due_date', title: 'Vencimento', canSort: true, field: 'due_date' },
        { id: 'days_overdue', title: 'Dias Atraso', canSort: true, field: 'days_overdue' },
        { id: 'status', title: 'Status', canSort: true, field: 'status' },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const [invoices, setInvoices] = useState<any[]>([]);

    const handleRowClick = async (row: any) => {
        setSelectedDebt(row);
        setLoadingMovements(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const res = await fetch(`${API_URL}/title-debts/${row.id}/movements`, {
                headers: { 'x-api-key': API_KEY, 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const data = await res.json();
            setMovements(data.data || []);
            setInvoices(data.invoices || []);
        } catch (e) {
            setMovements([]);
            setInvoices([]);
        }
        setLoadingMovements(false);
    };

    const pageSorted = (field: string, order: 'ASC' | 'DESC') => {
        setCurrentSortField(field);
        setCurrentSortOrder(order);
    };
    const pageChanged = (newPage: number) => setCurrentPage(newPage);
    const pageSizeChanged = (newSize: number) => { setCurrentPageSize(newSize); setCurrentPage(0); };

    const [paymentDebt, setPaymentDebt] = useState<any>(null);
    const [reversalDebt, setReversalDebt] = useState<any>(null);

    const renderRows = (rows: any[]) => {
        return rows.map((row: any) => (
            <tr key={row.id} className={`hover:bg-gray-50 cursor-pointer ${row.movements_count > 0 ? 'hover:bg-blue-50' : ''}`} onClick={() => handleRowClick(row)}>
                <td className="px-4 py-2 text-sm font-medium">{row.contract_number || '-'}</td>
                <td className="px-4 py-2 text-sm">{row.document_title || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500" title={row.contract_operation_id}>{row.contract_operation_id?.slice(0, 8)}</td>
                <td className="px-4 py-2 text-sm text-center">{row.installment_number || '-'}</td>
                <td className="px-4 py-2 text-sm text-center">{row.amendment_number > 0 ? row.amendment_number : '-'}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.amount)}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.paid_amount)}</td>
                <td className="px-4 py-2 text-sm">{formatDate(row.due_date)}</td>
                <td className="px-4 py-2 text-sm text-center">
                    {row.days_overdue > 0 ? <span className="text-red-600 font-medium">{row.days_overdue}d</span> : '-'}
                </td>
                <td className="px-4 py-2 text-sm">
                    <BadgeStatus text={row.status} color={statusColors[row.status] || 'gray'} />
                </td>
                <td className="px-4 py-2 text-sm" onClick={e => e.stopPropagation()}>
                    {row.status === 'OPEN' && (
                        <button
                            className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                            onClick={() => setPaymentDebt(row)}
                        >Baixa</button>
                    )}
                    {(row.status === 'PAID' || row.status === 'PARTIALLY_PAID') && (
                        <button
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 ml-1"
                            onClick={() => setReversalDebt(row)}
                        >Reverter</button>
                    )}
                </td>
            </tr>
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Títulos de Dívida"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Financeiro', url: '#' },
                    { label: 'Títulos de Dívida', url: '/settlement/title-debts' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={currentSortField}
                tableOrderSort={currentSortOrder}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={pageChanged}
                onItemsPerPageChange={pageSizeChanged}
                itemsLabel="títulos"
            >
                {{
                    filterSlot: (
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-4">
                                <PageSearch
                                    value={searchContract}
                                    label="Buscar por contrato..."
                                    onSearch={(val: string) => setSearchContract(val)}
                                    filterCleanVisible={true}
                                    filterCleanEnabled={!!searchContract}
                                    configureVisible={false}
                                />
                            </div>
                            <div className="col-span-3">
                                <select
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <option value="">Todos os status</option>
                                    <option value="OPEN">Em aberto</option>
                                    <option value="OPEN,PARTIALLY_PAID">Em aberto + Parcial</option>
                                    <option value="PARTIALLY_PAID">Pagamento parcial</option>
                                    <option value="PAID">Pago</option>
                                    <option value="PAID_WITH_DISCOUNT">Pago com desconto</option>
                                    <option value="SUPERSEDED">Renegociado</option>
                                    <option value="CANCELLED">Cancelado</option>
                                </select>
                            </div>
                            <div className="col-span-3">
                                <select
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    value={filterModality}
                                    onChange={(e) => setFilterModality(e.target.value)}
                                >
                                    <option value="">Todas as modalidades</option>
                                    <option value="LOAN">Empréstimo (CPRF PF/PJ)</option>
                                    <option value="CESSION">Cessão (Recebíveis)</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <select
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    value={filterNegotiation}
                                    onChange={(e) => setFilterNegotiation(e.target.value)}
                                >
                                    <option value="">Todas as negociações</option>
                                    <option value="ORIGINAL">Original</option>
                                    <option value="RENEGOTIATION">Renegociação</option>
                                    <option value="DEBT_CONFESSION">Confissão de Dívida</option>
                                    <option value="AGREEMENT">Acordo</option>
                                    <option value="CANCELLED">Cancelada</option>
                                </select>
                            </div>
                        </div>
                    ),
                    controlSlot: (<div></div>),
                }}
            </PageList>

            {selectedDebt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedDebt(null)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold">
                                Movimentações — Contrato {selectedDebt.contract_number} (Parcela {selectedDebt.installment_number || '-'})
                            </h2>
                            <button onClick={() => setSelectedDebt(null)} className="text-gray-500 hover:text-gray-700 text-xl">&times;</button>
                        </div>

                        <div className="mb-4 grid grid-cols-6 gap-4 text-sm bg-gray-50 p-3 rounded">
                            <div><span className="text-gray-500">Valor:</span> <strong>{formatCurrency(selectedDebt.amount)}</strong></div>
                            <div><span className="text-gray-500">Pago:</span> <strong>{formatCurrency(selectedDebt.paid_amount)}</strong></div>
                            <div>
                                {(selectedDebt.status === 'PAID' || selectedDebt.status === 'PAID_WITH_DISCOUNT') && parseFloat(selectedDebt.paid_amount) < parseFloat(selectedDebt.amount) && (
                                    <><span className="text-gray-500">Desconto:</span> <strong className="text-green-600">{formatCurrency(String(parseFloat(selectedDebt.amount) - parseFloat(selectedDebt.paid_amount)))}</strong></>
                                )}
                            </div>
                            <div><span className="text-gray-500">Vencimento:</span> <strong>{formatDate(selectedDebt.due_date)}</strong></div>
                            <div><span className="text-gray-500">Status:</span> <BadgeStatus text={selectedDebt.status} color={statusColors[selectedDebt.status] || 'gray'} /></div>
                            <div><span className="text-gray-500">Modalidade:</span> <strong>{selectedDebt.contract_modality || '-'}</strong></div>
                        </div>

                        {loadingMovements ? (
                            <p className="text-center text-gray-500 py-4">Carregando...</p>
                        ) : movements.length === 0 ? (
                            <p className="text-center text-gray-500 py-4">Nenhuma movimentação encontrada</p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Tipo</th>
                                        <th className="px-3 py-2 text-right">Valor</th>
                                        <th className="px-3 py-2 text-left">Data</th>
                                        <th className="px-3 py-2 text-left">Fonte Pgto</th>
                                        <th className="px-3 py-2 text-left">Liquidação</th>
                                        <th className="px-3 py-2 text-left">Conta</th>
                                        <th className="px-3 py-2 text-left">Nosso Nº</th>
                                        <th className="px-3 py-2 text-right">Juros</th>
                                        <th className="px-3 py-2 text-right">Custas</th>
                                        <th className="px-3 py-2 text-left">Obs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movements.map((m: any) => (
                                        <tr key={m.id} className="border-t hover:bg-gray-50">
                                            <td className="px-3 py-2">
                                                <span className={`inline-flex items-center gap-1 ${m.movement_type === 'REVERSAL' ? 'text-red-600' : ''}`}>
                                                    {movementTypeLabels[m.movement_type] || m.movement_type}
                                                    {m.invoice?.is_subrogation && <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded">Sub</span>}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right font-medium">{formatCurrency(m.amount)}</td>
                                            <td className="px-3 py-2">{formatDate(m.movement_date)}</td>
                                            <td className="px-3 py-2">{m.payment_source || '-'}</td>
                                            <td className="px-3 py-2">{settlementTypeLabels[m.settlement_type] || '-'}</td>
                                            <td className="px-3 py-2 text-xs">{m.invoice?.bank_account || '-'}</td>
                                            <td className="px-3 py-2 text-xs font-mono">{m.invoice?.our_number || '-'}</td>
                                            <td className="px-3 py-2 text-right">{parseFloat(m.interest_amount || '0') > 0 ? formatCurrency(m.interest_amount) : '-'}</td>
                                            <td className="px-3 py-2 text-right">{parseFloat(m.court_costs || '0') > 0 ? formatCurrency(m.court_costs) : '-'}</td>
                                            <td className="px-3 py-2 text-xs text-gray-600 max-w-[200px] truncate" title={m.invoice?.notes || m.notes || ''}>
                                                {m.invoice?.notes || m.notes || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {invoices.length > 0 && (
                            <div className="mt-6">
                                <h3 className="text-sm font-bold text-gray-700 mb-2">Boletos / Invoices Emitidos</h3>
                                <table className="w-full text-sm">
                                    <thead className="bg-blue-50">
                                        <tr>
                                            <th className="px-3 py-2 text-right">Valor</th>
                                            <th className="px-3 py-2 text-left">Vencimento</th>
                                            <th className="px-3 py-2 text-left">Status</th>
                                            <th className="px-3 py-2 text-left">Conta</th>
                                            <th className="px-3 py-2 text-left">Nosso Nº</th>
                                            <th className="px-3 py-2 text-left">Meio</th>
                                            <th className="px-3 py-2 text-left">Obs</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((inv: any) => (
                                            <tr key={inv.id} className="border-t hover:bg-blue-50">
                                                <td className="px-3 py-2 text-right font-medium">{formatCurrency(inv.amount)}</td>
                                                <td className="px-3 py-2">{formatDate(inv.due_date)}</td>
                                                <td className="px-3 py-2">
                                                    <BadgeStatus text={inv.status || 'OPEN'} color={statusColors[inv.status] || 'yellow'} />
                                                </td>
                                                <td className="px-3 py-2 text-xs">{inv.bank_account || '-'}</td>
                                                <td className="px-3 py-2 text-xs font-mono">{inv.our_number || '-'}</td>
                                                <td className="px-3 py-2 text-xs">{inv.payment_method || '-'}</td>
                                                <td className="px-3 py-2 text-xs text-gray-600 max-w-[150px] truncate" title={inv.notes || ''}>{inv.notes || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {paymentDebt && (
                <PaymentModal
                    titleDebt={paymentDebt}
                    onClose={() => setPaymentDebt(null)}
                    onSuccess={() => setRefreshKey(k => k + 1)}
                />
            )}
            {reversalDebt && (
                <ReversalModal
                    titleDebt={reversalDebt}
                    onClose={() => setReversalDebt(null)}
                    onSuccess={() => setRefreshKey(k => k + 1)}
                />
            )}
        </>
    );
};

export default TitleDebtPageList;
